/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { EditFilled } from '@ant-design/icons';
import { CONST } from '@common/constants/const.constant';
import { Countries } from '@common/constants/countries.constant';
import { THEME } from '@common/constants/theme.constant';
import { IFieldProps } from '@common/general-ui/field/field.component';
import { getInputLoqateLabel, IInputLoqateValue } from '@common/general-ui/input-loqate/input-loqate.component';
import { InputV2 } from '@common/general-ui/input-v2/input-v2.component';
import { Input } from '@common/general-ui/input/input.component';
import { Link } from '@common/general-ui/link/link.component';
import { ISelectChangeHandler, Select } from '@common/general-ui/select/select.component';
import { SimpleCollapse } from '@common/general-ui/simple-collapse/simple-collapse.component';
import { t } from '@common/hooks/use-t.hook';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { css } from 'twin.macro';

export type IAddressChangeHandler = (arg: { name: string; value: IInputLoqateValue }) => void;
export interface IAddressProps<TName = string> extends IFieldProps<IInputLoqateValue, TName> {
    className?: string;
    onChange?: IAddressChangeHandler;
    onBlur?: (e: React.FocusEvent) => void;
    mode?: 'oneLineEditable' | 'allEditable';
    showCollapsePanel?: boolean;
    collapsePanelLabel?: ReactNode;
}

export const Address = ({
    className,
    showCollapsePanel,
    collapsePanelLabel,
    mode = 'oneLineEditable',
    ...rest
}: IAddressProps): JSX.Element => {
    const [value, setValue] = useState<IInputLoqateValue | undefined>(rest.defaultValue || rest.value);
    const [blur, setBlur] = useState<{ [key in keyof IInputLoqateValue]: boolean }>({
        addressLine1: false,
        addressLine2: false,
        addressLine3: false,
        city: false,
        country: false,
        label: false,
        originalLabel: false,
        postCode: false,
    });
    const [isEditMode, setEditMode] = useState(false);

    useEffect(() => {
        if (rest.value) {
            setValue({
                ...rest.value,
                label: getInputLoqateLabel(rest.value),
                originalLabel: getInputLoqateLabel(rest.value),
            });
        }
    }, [rest.value]);

    const createAddressChangeHandler = useCallback(
        (propertyName: keyof IInputLoqateValue) => {
            return (e: React.ChangeEvent<HTMLInputElement>): void => {
                setValue((val: IInputLoqateValue | undefined): IInputLoqateValue => {
                    const newValue = {
                        ...val,
                        [propertyName]: e.target.value,
                    } as IInputLoqateValue;
                    newValue.label = getInputLoqateLabel(newValue);
                    newValue.originalLabel = getInputLoqateLabel(newValue);
                    rest.onChange && rest.onChange({ name: rest.name, value: newValue });
                    return newValue;
                });
            };
        },
        [rest],
    );

    const createBlurHandler = useCallback(
        (propertyName: keyof IInputLoqateValue) => (): void => {
            setBlur((prev) => {
                return {
                    ...prev,
                    [propertyName]: true,
                };
            });
        },
        [],
    );

    const handleCountryChange = useCallback<ISelectChangeHandler>(
        (e) => {
            setValue((val: IInputLoqateValue | undefined): IInputLoqateValue => {
                const newValue = {
                    ...val,
                    country: e.value,
                } as IInputLoqateValue;
                newValue.label = getInputLoqateLabel(newValue);
                newValue.originalLabel = getInputLoqateLabel(newValue);
                rest.onChange && rest.onChange({ name: rest.name, value: newValue });
                return newValue;
            });
        },
        [rest],
    );

    const createGetErrorMessage = (propertyName: keyof IInputLoqateValue, required: boolean): string | undefined => {
        const val = value ? value[propertyName] : undefined;
        if (!blur[propertyName]) {
            return;
        }
        if (required && !val) {
            return CONST.thisFieldRequired;
        }
        return undefined;
    };

    const countryOptions = useMemo(() => {
        return Countries.map((c) => {
            return {
                label: c.name,
                value: c['alpha-2'],
            };
        });
    }, []);

    const renderOneLineEditable = (): JSX.Element => {
      const addressLine = `${value?.addressLine2 && `${value.addressLine2}, `} ${value?.addressLine3 && `${value.addressLine3}, `} ${value?.city}, ${value?.postCode}`;
        return (
            <div css={styles.editableContainer} tw="py-2">
                <div tw="flex">
                    {!value?.addressLine1 && !isEditMode ? (
                        <div>
                            <span css={styles.error} data-testid="first-line-warning">
                                First line not found
                            </span>
                            <Link name="edit" onClick={(): void => setEditMode(true)}>
                                <EditFilled /> Edit first line
                            </Link>
                        </div>
                    ) : (
                        <div tw="w-[100%] flex flex-col gap-[4px]">
                            <label css={styles.fieldLabel}>Address Line 1</label>
                            <InputV2
                                name="line1"
                                type="text"
                                value={value?.addressLine1}
                                onChange={createAddressChangeHandler('addressLine1')}
                                onBlur={(): void => {
                                    createBlurHandler('addressLine1')();
                                    // setEditMode(!!rest?.value?.addressLine1);
                                }}
                                errorMessage={createGetErrorMessage('addressLine1', true)}
                            />
                        </div>
                    )}
                </div>
                <div tw="flex">
                  {addressLine}
                </div>
            </div>
        );
    };

    const renderAllEditable = (): JSX.Element => {
        return (
            <div tw="grid lg:grid-cols-2 gap-4 py-2" className={className}>
                <Input
                    name="line1"
                    required
                    label={t('Address line 1')}
                    type="text"
                    value={value?.addressLine1}
                    errorMessage={createGetErrorMessage('addressLine1', true)}
                    onBlur={createBlurHandler('addressLine1')}
                    onChange={createAddressChangeHandler('addressLine1')}
                />
                <Input
                    name="line2"
                    label={t('Address line 2')}
                    type="text"
                    value={value?.addressLine2}
                    errorMessage={createGetErrorMessage('addressLine2', false)}
                    onBlur={createBlurHandler('addressLine2')}
                    onChange={createAddressChangeHandler('addressLine2')}
                />
                <Input
                    name="line2"
                    label={t('Address line 3')}
                    type="text"
                    value={value?.addressLine3}
                    errorMessage={createGetErrorMessage('addressLine3', false)}
                    onBlur={createBlurHandler('addressLine3')}
                    onChange={createAddressChangeHandler('addressLine3')}
                />
                <Input
                    name="city"
                    required
                    label={t('City')}
                    type="text"
                    value={value?.city}
                    errorMessage={createGetErrorMessage('city', true)}
                    onBlur={createBlurHandler('city')}
                    onChange={createAddressChangeHandler('city')}
                />
                <Select
                    name="country"
                    required
                    label={t('Country')}
                    options={countryOptions}
                    value={value?.country}
                    showSearch={true}
                    allowClear={true}
                    errorMessage={createGetErrorMessage('country', true)}
                    onBlur={createBlurHandler('country')}
                    onChange={handleCountryChange}
                />
                <Input
                    name="postCode"
                    required
                    label={t('Postcode')}
                    type="text"
                    value={value?.postCode}
                    errorMessage={createGetErrorMessage('postCode', true)}
                    onBlur={createBlurHandler('postCode')}
                    onChange={createAddressChangeHandler('postCode')}
                />
            </div>
        );
    };

    return (
        <>
            {mode === 'oneLineEditable' ? (
                showCollapsePanel ? (
                    <SimpleCollapse
                        className={className}
                        label={
                            <span tw="font-normal" css={styles.editText}>
                                {' '}
                                {collapsePanelLabel || 'Edit delivery address'}
                            </span>
                        }
                    >
                        {renderOneLineEditable()}
                    </SimpleCollapse>
                ) : (
                    renderOneLineEditable()
                )
            ) : showCollapsePanel ? (
                <SimpleCollapse
                    icon={false}
                    className={className}
                    label={<span tw="font-normal"> {collapsePanelLabel || 'Edit billing address'}</span>}
                >
                    {renderAllEditable()}
                </SimpleCollapse>
            ) : (
                renderAllEditable()
            )}
        </>
    );
};

const styles = {
    error: css`
        color: red;
    `,
    editText: css`
        color: black;
        font-weight: 300;
        font-size: 14px;
    `,
    fieldLabel: css`
        font-weight: 400;
    `,
    editableContainer: css`
        border: 1px solid ${THEME.GRAY_200};
        background-color: ${THEME.GRAY_200};
        padding: 12px;
        border-radius: 6px;
        display: flex;
        flex-direction: column;
        gap: 4px;
    `,
};

export const validateBillingAddress = (val: IInputLoqateValue): string | undefined => {
    return !val.addressLine1 || !val.city || !val.postCode || !val.country ? CONST.thisFieldRequired : undefined;
};
export const validateShippingAddress = (val: IInputLoqateValue): string | undefined => {
    return !val.addressLine1 ? CONST.thisFieldRequired : undefined;
};
